import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CalendarDays, CheckCircle2, Loader2, MapPin, Ticket, Users } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { DualPrice } from "@/components/DualPrice";
import { TripView } from "@/components/owner/TripView";

const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

export default function TourPage() {
  const { publicToken } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", pax: "1", arrival_date: "", arrival_time: "", departure_date: "", notes: "", car_option_id: "" });
  const [sending, setSending] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/tours/${publicToken}`)
      .then((res) => setTrip(res.data.trip))
      .catch(() => setError(true));
  }, [publicToken]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const signup = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    if ((trip?.car_options || []).length > 0 && !form.car_option_id) {
      toast.error("Please pick your car — first come, first served.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/tours/${publicToken}/signup`, form);
      setBooked(res.data);
      setTrip((t) => ({
        ...t,
        spots_taken: (t.spots_taken || 0) + (parseInt(form.pax, 10) || 1),
        spots_left: t.spots_left === null || t.spots_left === undefined ? t.spots_left : t.spots_left - (parseInt(form.pax, 10) || 1),
        car_options: (t.car_options || []).map((o) => (o.option_id === form.car_option_id ? { ...o, remaining: Math.max(0, o.remaining - 1) } : o)),
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reserve right now.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400";

  const soldOut = trip && trip.spots_left !== null && trip.spots_left !== undefined && trip.spots_left <= 0;

  return (
    <div data-testid="tour-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20">
        <div className="flex flex-col items-center text-center">
          <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto" />
        </div>

        {error && (
          <div data-testid="tour-not-found" className="mt-14 border border-dashed border-neutral-700 py-20 text-center">
            <p className="font-serif text-3xl font-light text-neutral-300">This tour is not available.</p>
            <p className="mt-3 text-sm text-neutral-500">The link may be incomplete or the tour has closed.</p>
          </div>
        )}
        {!error && !trip && (
          <div className="mt-20 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
          </div>
        )}

        {trip && booked && (
          <motion.div
            data-testid="tour-booked"
            className="mt-12 border border-emerald-500/30 bg-emerald-500/5 p-8 text-center sm:p-12"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-300" />
            <h1 className="mt-4 font-luxe text-2xl uppercase tracking-[0.15em] text-neutral-50 sm:text-3xl">
              You&apos;re in
            </h1>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-400">
              Your place on <span className="text-amber-100">{trip.client.name}</span> is reserved — party of {form.pax}.
              We&apos;ve emailed your personal itinerary link. Keep it safe: it always shows the latest schedule,
              and you can request changes from your page at any time.
            </p>
            {booked.link && (
              <a
                data-testid="tour-booked-link"
                href={booked.link}
                className="mt-6 inline-block bg-amber-500 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
              >
                Open My Itinerary
              </a>
            )}
          </motion.div>
        )}

        {trip && !booked && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            {trip.cover_image && (
              <div data-testid="tour-cover" className="mt-10 overflow-hidden border border-amber-500/20">
                <img src={trip.cover_image} alt={trip.client.name} className="h-64 w-full object-cover sm:h-96" />
              </div>
            )}
            <div className="mt-10 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-300/80">
                Open tour — limited places
              </p>
              <h1 className="mt-4 font-luxe text-3xl uppercase leading-snug tracking-[0.12em] text-neutral-50 sm:text-4xl">
                {trip.client.name}
              </h1>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                {(trip.arrival_date || trip.departure_date) && (
                  <span className="flex items-center gap-2 border border-amber-500/25 px-3 py-1.5">
                    <CalendarDays className="h-3.5 w-3.5 text-amber-400/70" />
                    {fmtDate(trip.arrival_date)} — {fmtDate(trip.departure_date)}
                  </span>
                )}
                {trip.location && (
                  <span className="flex items-center gap-2 border border-amber-500/25 px-3 py-1.5">
                    <MapPin className="h-3.5 w-3.5 text-amber-400/70" />
                    {trip.location}
                  </span>
                )}
                {trip.spots_left !== null && trip.spots_left !== undefined && (
                  <span data-testid="tour-spots-left" className="flex items-center gap-2 border border-amber-500/25 px-3 py-1.5">
                    <Users className="h-3.5 w-3.5 text-amber-400/70" />
                    {soldOut ? "Sold out" : `${trip.spots_left} of ${trip.capacity} places left`}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-10 border border-amber-500/20 bg-[#0c0c10]/80 p-6 sm:p-10">
              <TripView trip={trip} />
            </div>

            {trip.ticket_price && (
              <div data-testid="tour-ticket-price" className="mt-6 border border-amber-500/30 bg-amber-500/5 p-5 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
                  <Ticket className="mr-2 inline h-3.5 w-3.5" />
                  Ticket — per person
                </p>
                <p className="mt-2 font-luxe text-3xl tracking-wide text-amber-100">
                  <DualPrice value={trip.ticket_price} />
                </p>
              </div>
            )}

            {!soldOut ? (
              <div data-testid="tour-signup" className="mt-10 border border-amber-500/25 bg-[#0c0c10] p-6 sm:p-10">
                <p className="text-center text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-300/80">
                  Reserve your place
                </p>
                <p className="mx-auto mt-3 max-w-md text-center text-sm leading-relaxed text-neutral-400">
                  Everyone arrives on their own schedule — tell us yours so we can look after you from the moment you land.
                </p>
                <div className="mt-8 space-y-4">
                  <input data-testid="tour-name-input" placeholder="Your full name *" value={form.name} onChange={setField("name")} className={inputCls} />
                  <input data-testid="tour-email-input" type="email" placeholder="Your email *" value={form.email} onChange={setField("email")} className={inputCls} />
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="block">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Are you travelling solo or as two?</span>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {[["1", "Just me"], ["2", "Two of us"]].map(([n, label]) => (
                          <button
                            key={n}
                            type="button"
                            data-testid={`tour-pax-${n}`}
                            onClick={() => setForm((f) => ({ ...f, pax: n }))}
                            aria-pressed={form.pax === n}
                            className={`border px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
                              form.pax === n
                                ? "border-amber-400/70 bg-amber-500/15 text-amber-200"
                                : "border-neutral-700 text-neutral-400 hover:border-amber-500/40 hover:text-amber-100"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      {form.pax === "1" && (
                        <p data-testid="tour-pax-solo-note" className="mt-2 text-[10px] leading-relaxed text-neutral-500">
                          Travelling solo — dinners are billed at half price for one.
                        </p>
                      )}
                    </div>
                    <label className="block">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">You arrive</span>
                      <input data-testid="tour-arrival-date" type="date" value={form.arrival_date} onChange={setField("arrival_date")} className={`${inputCls} mt-2`} />
                    </label>
                    <label className="block">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Arrival time</span>
                      <input data-testid="tour-arrival-time" type="time" value={form.arrival_time} onChange={setField("arrival_time")} className={`${inputCls} mt-2`} />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">You depart (optional)</span>
                    <input data-testid="tour-departure-date" type="date" value={form.departure_date} onChange={setField("departure_date")} className={`${inputCls} mt-2`} />
                  </label>
                  {(trip.car_options || []).length > 0 && (
                    <div className="block">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Your car — first come, first served</span>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {trip.car_options.map((o) => {
                          const gone = o.remaining <= 0;
                          const picked = form.car_option_id === o.option_id;
                          return (
                            <button
                              key={o.option_id}
                              type="button"
                              data-testid={`tour-car-${o.option_id}`}
                              disabled={gone}
                              onClick={() => setForm((f) => ({ ...f, car_option_id: o.option_id }))}
                              aria-pressed={picked}
                              className={`border px-4 py-3 text-left transition-colors ${
                                gone
                                  ? "cursor-not-allowed border-neutral-800 text-neutral-600 line-through"
                                  : picked
                                    ? "border-amber-400/70 bg-amber-500/15 text-amber-200"
                                    : "border-neutral-700 text-neutral-300 hover:border-amber-500/40 hover:text-amber-100"
                              }`}
                            >
                              <span className="block text-xs font-semibold uppercase tracking-[0.15em]">{o.label}</span>
                              <span className="mt-1 block text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                                {gone ? "All taken" : `${o.remaining} of ${o.qty} left`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <textarea
                    data-testid="tour-notes-input"
                    rows={2}
                    placeholder="Anything we should know — dietary, celebrations, requests..."
                    value={form.notes}
                    onChange={setField("notes")}
                    className={`${inputCls} resize-none`}
                  />
                  <button
                    data-testid="tour-signup-button"
                    onClick={signup}
                    disabled={sending}
                    className="flex w-full items-center justify-center gap-2 bg-amber-500 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300 disabled:opacity-60"
                  >
                    {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                    Reserve My Place
                  </button>
                </div>
              </div>
            ) : (
              <div data-testid="tour-sold-out" className="mt-10 border border-dashed border-neutral-700 py-12 text-center">
                <p className="font-serif text-2xl font-light text-neutral-300">This tour is fully booked.</p>
              </div>
            )}
          </motion.div>
        )}

        <div className="mt-12 text-center">
          <Link to="/" className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 transition-colors hover:text-amber-200">
            Exclusive Reykjavik · Key to the city — and beyond
          </Link>
        </div>
      </div>
    </div>
  );
}
